import ReactPaginate from 'react-paginate';
import clsx from 'clsx';
import type { IDataTablePaginationComponentCommonProps, IDataTablePaginationSharedProps } from '../shared/types';

/**
 * DataTablePagination component'inin props interface'i.
 * ReactPaginate props'larını genişletir ve DataTable'a özel prop'lar ekler.
 */
export interface IDataTablePaginationProps extends IDataTablePaginationSharedProps, IDataTablePaginationComponentCommonProps {}

/**
 * DataTablePagination Component
 *
 * Sayfa numaralarını, önceki/sonraki butonlarını ve sayfa aralığını gösterir.
 * ReactPaginate kütüphanesini kullanır.
 *
 * Özellikler:
 * - Sayfa numaraları (örnek: 1, 2, 3, ... 10)
 * - Önceki/Sonraki butonları
 * - Sayfa aralığı gösterimi (örnek: ... 5, 6, 7 ...)
 * - Aktif sayfa vurgulama
 * - İlk/son sayfada buton devre dışı bırakma
 *
 * Varsayılan davranış:
 * - pageRangeDisplayed: 2 (aktif sayfanın her iki yanında gösterilecek sayfa sayısı)
 * - marginPagesDisplayed: 2 (başta ve sonda gösterilecek sayfa sayısı)
 * - breakLabel: '...' (sayfa aralığı göstergesi)
 *
 * @param props - Pagination props'ları
 * @param props.setPagination - Pagination durumunu güncelleme fonksiyonu
 * @param props.paginationState - Mevcut pagination durumu
 * @param props.paginationMeta - Pagination meta bilgileri (pageCount hesaplamak için)
 * @param props.previousLabelWrapperClassName - Önceki buton wrapper'ı için CSS sınıfı
 * @param props.previousLabelBtnClassName - Önceki buton için CSS sınıfı
 * @param props.previousLabelIconClassName - Önceki buton ikonu için CSS sınıfı
 * @param props.nextLabelWrapperClassName - Sonraki buton wrapper'ı için CSS sınıfı
 * @param props.nextLabelBtnClassName - Sonraki buton için CSS sınıfı
 * @param props.nextLabelIconClassName - Sonraki buton ikonu için CSS sınıfı
 * @param props.activeClassName - Aktif sayfa için CSS sınıfı (varsayılan: 'active z-index-2')
 * @param props.containerClassName - Pagination container için CSS sınıfı (varsayılan: 'pagination')
 * @param props.pageClassName - Sayfa numarası item'ı için CSS sınıfı (varsayılan: 'page-item')
 * @param props.pageLinkClassName - Sayfa numarası link'i için CSS sınıfı
 * @param props.breakClassName - Sayfa aralığı göstergesi için CSS sınıfı
 * @param props.rest - Diğer ReactPaginate props'ları (onPageChange, forcePage, pageCount hariç)
 *
 * @returns JSX.Element | null - Pagination UI'ı veya null (eğer pageCount < 1 ise)
 */
export default function DataTablePagination(props: IDataTablePaginationProps) {
  const {
    // Core
    setPagination,
    paginationState,
    paginationMeta,

    // Other Customizable Props
    previousLabelWrapperClassName,
    previousLabelBtnClassName,
    previousLabelIconClassName,
    nextLabelWrapperClassName,
    nextLabelBtnClassName,
    nextLabelIconClassName,

    // ReactPaginateProps
    activeClassName,
    containerClassName,
    pageClassName,
    pageLinkClassName,
    breakClassName,
    ...rest
  } = props;

  if (paginationMeta?.pageCount < 1) return null;
  return (
    <ReactPaginate
      breakLabel={'...'}
      pageRangeDisplayed={2}
      marginPagesDisplayed={2}
      {...rest}
      activeClassName={clsx('active z-index-2', activeClassName)}
      containerClassName={clsx('pagination', containerClassName)}
      pageClassName={clsx('page-item', pageClassName)}
      pageLinkClassName={clsx('page-link btn btn-icon btn-sm btn-light mr-2 my-1', pageLinkClassName)}
      breakClassName={clsx('page-link mr-2 my-1 mx-2', breakClassName)}
      forcePage={paginationState.pageIndex}
      onPageChange={({ selected }) => setPagination((prev) => ({ ...prev, pageIndex: selected }))}
      pageCount={paginationMeta?.pageCount || 1}
      previousLabel={
        <div className={clsx('page-item previous mx-2', previousLabelWrapperClassName)}>
          <button disabled={Boolean(paginationState.pageIndex <= 1)} className={clsx('btn btn-icon btn-sm btn-light mr-2 my-1 page-link', previousLabelBtnClassName)}>
            <i className={clsx('previous', previousLabelIconClassName)}></i>
          </button>
        </div>
      }
      nextLabel={
        <div className={clsx('page-item next mx-2', nextLabelWrapperClassName)}>
          <button
            disabled={Boolean(
              (paginationMeta?.pageCount && paginationState.pageIndex && paginationMeta?.pageCount === paginationState.pageIndex) || paginationMeta?.pageCount === 0,
            )}
            className={clsx('btn btn-icon btn-sm btn-light mr-2 my-1 page-link', nextLabelBtnClassName)}
          >
            <i className={clsx('next', nextLabelIconClassName)}></i>
          </button>
        </div>
      }
    />
  );
}
